import { Button, Spinner } from '@immo/ui-kit';
import type { FleetCommandView, FleetLaneCommandVerb, FleetProfileStatus } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { TerminalIcon } from '@/components/layout/icons';
import { useCommandVerbCopy } from './command-verbs';
import { useCommandStepLabel } from './command-outcome-badge';
import { commandStep } from './command-steps';

type Props = {
  readonly approved: boolean;
  readonly canCommand: boolean;
  /** The lane as its host last reported it — what decides between Start and Stop. */
  readonly profile: Pick<FleetProfileStatus, 'state' | 'title' | 'fields'>;
  /** This lane's oldest unsettled `start` / `stop` / `restart`, or `null`. */
  readonly pending: FleetCommandView | null;
  readonly viewerOpen: boolean;
  /** Opens the confirm for one lane verb (fleet-agent ADR 0013). */
  readonly onLaneVerb: (verb: FleetLaneCommandVerb) => void;
  /** Opens the `engine` dialog on this lane. */
  readonly onEngine: () => void;
  readonly onToggleViewer: () => void;
};

/** The phase a host reports for a lane the operator stopped (fleet-agent ADR 0013) — `…` adds "after its round". */
const STOPPED_BY_OPERATOR = 'stopped by the operator';

/**
 * Whether the host says the operator stopped this lane. The phase rides the untyped status as the `Phase` field (and
 * as the title when no ticket is held), so both are read.
 */
export function isStoppedByOperator(profile: Pick<FleetProfileStatus, 'state' | 'title' | 'fields'>): boolean {
  if (profile.state !== 'stopped') return false;

  const phase = profile.fields.find((field) => field.label === 'Phase')?.value ?? profile.title ?? '';

  return phase.startsWith(STOPPED_BY_OPERATOR);
}

/**
 * The profile-card footer: the lane's own lifecycle verbs, its engine, and the log toggle.
 *
 * fleet-agent ADR 0013 brought per-lane control back. `stop` and `start` are mutually exclusive on the lane's own
 * reported state: a running lane offers Stop (its dialog can let the round in hand finish first), a lane its host
 * reports stopped by the operator offers Start. A lane stopped for any other reason gets no Start — nothing of the
 * operator's holds it, so there is nothing to take back — but Restart, which starts it if it is down, is offered on
 * every lane. While one of these is in flight the three are replaced by its step, not disabled: a second press before
 * the first settles is noise the host would answer `already-running` / `already-stopped`.
 *
 * `engine` opens the host's engine setting on this lane (fleet-agent 0.19.0). Every verb requires `canCommand` —
 * absent, never disabled (ADR 0015). Nothing renders for a non-approved machine — every verb (`logs` included) is
 * refused by `IssueFleetCommandCommandHandler` there. The toggle stays available to a read-only operator.
 */
export function ProfileCommands({
  approved,
  canCommand,
  profile,
  pending,
  viewerOpen,
  onLaneVerb,
  onEngine,
  onToggleViewer,
}: Props) {
  const t = useTranslations('pages.admin.fleet.log');

  if (!approved) return null;

  const running = profile.state !== 'stopped';

  return (
    <div className="flex flex-col gap-2 border-t border-border px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:px-5">
      {canCommand && pending !== null && <PendingLaneVerb command={pending} />}
      {canCommand && pending === null && (
        <>
          {running && <VerbButton verb="stop" onClick={() => onLaneVerb('stop')} />}
          {isStoppedByOperator(profile) && <VerbButton verb="start" onClick={() => onLaneVerb('start')} />}
          <VerbButton verb="restart" onClick={() => onLaneVerb('restart')} />
        </>
      )}
      {canCommand && <VerbButton verb="engine" onClick={onEngine} />}
      <span className="hidden flex-1 sm:block" />
      <Button type="button" size="sm" variant="ghost" className="w-full sm:w-auto" onClick={onToggleViewer}>
        <TerminalIcon className="h-4 w-4" aria-hidden />
        {viewerOpen ? t('toggle-close') : t('toggle-open')}
      </Button>
    </div>
  );
}

function VerbButton({
  verb,
  onClick,
}: {
  readonly verb: FleetLaneCommandVerb | 'engine';
  readonly onClick: () => void;
}) {
  const copy = useCommandVerbCopy(verb);
  const Icon = copy.icon;

  return (
    <Button type="button" size="sm" variant={copy.variant} className="w-full sm:w-auto" onClick={onClick}>
      <Icon className="h-4 w-4" aria-hidden />
      {copy.label}
    </Button>
  );
}

function PendingLaneVerb({ command }: { readonly command: FleetCommandView }) {
  const copy = useCommandVerbCopy(command.verb as FleetLaneCommandVerb);
  const stepLabel = useCommandStepLabel(commandStep(command));

  return (
    <span className="flex items-center gap-2 text-base-sm text-text-tertiary" role="status">
      <Spinner size="sm" aria-hidden />
      {copy.label} — {stepLabel}
    </span>
  );
}
